import * as React from 'react';
import { Box, Table, TableContainer, TablePagination, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Loading } from 'components';
import { ETableOrder, IEnhancedTableConfig, IEnhancedTableHeadCell } from 'models';
import { EnhancedTableBody } from './enhanced-table-body';
import { EnhancedTableHead } from './enhanced-table-head';
import { EnhancedTableToolbar } from './enhanced-table-toolbar';
import { useEnhancedTableFilters } from './use-enhanced-table-filters';

export interface EnhancedTableProps {
  config?: IEnhancedTableConfig;
  loading: boolean;
  loadingLabel: string;
  noDataLabel: string;
  noDataSectionHeight?: number;
  toolbarTitle?: string;
  data: Array<any>;
  convertFunctionForDataToTableData?: Function;
  headColumns: Array<IEnhancedTableHeadCell>;
  columnKeys: Array<string>;
  rowActions: {
    onRow?: Function;
    onEdit?: Function;
    onDelete?: Function;
    onReview?: Function;
  };
  initialRowsPerPage?: number;
  deleteFunction?: (payload: any) => void;
  defaultSortColumnName?: string;
  defaultSortDirection?: ETableOrder;
  styles?: any;
  enhancedTableHeadClassNames?: any;
  visiblePagination?: boolean;
  selected?: any[];
  setSelected?: (selected: any[]) => void;
  onFilteredDataChange?: (filteredData: any[]) => void;
}

export const EnhancedTable: React.FC<EnhancedTableProps> = (props) => {
  const {
    config,
    loading,
    loadingLabel,
    noDataLabel,
    noDataSectionHeight = 240,
    toolbarTitle = '',
    data,
    convertFunctionForDataToTableData,
    headColumns,
    columnKeys,
    rowActions,
    initialRowsPerPage,
    deleteFunction,
    defaultSortColumnName,
    defaultSortDirection,
    styles,
    enhancedTableHeadClassNames,
    visiblePagination = true,
    selected = [],
    setSelected = () => {},
    onFilteredDataChange,
  } = props;
  const [order, setOrder] = React.useState<ETableOrder>(defaultSortDirection || 'asc');
  const [orderBy, setOrderBy] = React.useState<string>(defaultSortColumnName || 'name');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(initialRowsPerPage || 10);
  const {
    defaultCheckboxFilters,
    checkboxFilters,
    textFilters,
    filteredData,
    clearFilters,
    updateCheckboxFilters,
    selectAllCheckboxFilters,
    unselectAllCheckboxFilters,
    updateTextFilters,
  } = useEnhancedTableFilters({ data, headColumns, onFilteredDataChange });

  const useStyles = makeStyles({
    root: {
      '&&': {
        width: '100%',
      },
    },
  });

  const classes = useStyles();

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredData.map((n) => `${n.id}`);
      setSelected(newSelected);
      return;
    }

    setSelected([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onDelete = () => {
    if (deleteFunction) {
      deleteFunction(selected);
    }
  };

  React.useEffect(() => {
    if (initialRowsPerPage) {
      setRowsPerPage(initialRowsPerPage);
    }
  }, [initialRowsPerPage]);

  const definedRowsPerPageOptions = [
    {
      value: 5,
      label: '5',
    },
    {
      value: 10,
      label: '10',
    },
    {
      value: 25,
      label: '25',
    },
    {
      value: filteredData.length,
      label: 'All',
    },
  ];

  return (
    <Box
      className={classes.root}
      sx={{ bgcolor: 'white', borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflow: 'hidden' }}
    >
      {loading ? (
        <Box height={300} display="flex" justifyContent="center" alignItems="center">
          <Loading text={loadingLabel} />
        </Box>
      ) : (
        <>
          {(!config || config?.toolbar) && (
            <EnhancedTableToolbar
              title={toolbarTitle}
              numSelected={selected.length}
              defaultCheckboxFilters={defaultCheckboxFilters}
              checkboxFilters={checkboxFilters}
              textFilters={textFilters}
              hideclearFilters={config?.hideClearFilters}
              clearFilters={clearFilters}
              onDelete={onDelete}
            />
          )}

          <TableContainer>
            <Table
              size={'medium'}
              aria-label="enhanced table"
              style={{
                minWidth: 750,
                ...styles?.tableStyle,
              }}
            >
              <EnhancedTableHead
                checkable={!config || config?.rowCheckable}
                actionVisible={!config || config?.rowActionVisible}
                columns={headColumns}
                defaultCheckboxFilters={defaultCheckboxFilters}
                checkboxFilters={checkboxFilters}
                textFilters={textFilters}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                rowCount={filteredData.length}
                onCheckboxFilterItem={updateCheckboxFilters}
                onCheckboxFilterSelectAll={selectAllCheckboxFilters}
                onCheckboxFilterUnselectAll={unselectAllCheckboxFilters}
                onChangeTextFilter={updateTextFilters}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                classNames={enhancedTableHeadClassNames}
              />

              {filteredData.length > 0 && (
                <EnhancedTableBody
                  checkable={!config || config?.rowCheckable}
                  actionVisible={!config || config?.rowActionVisible}
                  data={filteredData}
                  convertFunctionForDataToTableData={convertFunctionForDataToTableData}
                  columns={headColumns}
                  columnKeys={columnKeys}
                  order={order}
                  orderBy={orderBy}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  selected={selected}
                  setSelected={setSelected}
                  actions={rowActions}
                />
              )}
            </Table>
          </TableContainer>

          {visiblePagination && filteredData.length > 0 && data.length > (initialRowsPerPage || 10) && (
            <TablePagination
              rowsPerPageOptions={definedRowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}

          {filteredData.length === 0 && (
            <Box display="flex" width="100%" height={noDataSectionHeight} alignItems="center" justifyContent="center">
              <Typography color="textSecondary" noWrap>
                {noDataLabel}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
